import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSettings,
  updateLocationSettings,
  type LocationSettingsUpdateResult,
  type UpdateLocationSettingsInput,
} from "../requests/settings.requests.ts";
import { GetSettingsParams } from "../../types/settings.ts";
import { QUERY_KEYS } from "../config.ts";
import type { ApiResponse } from "@/shared/types/api.ts";

/**
 * Hook to fetch settings context based on scope
 * @param params - Query parameters for scope and id
 * @param enabled - Enable/disable the query
 * @returns React Query result with settings context
 */
export const useGetSettings = (params?: GetSettingsParams, enabled: boolean = true) => {
  return useQuery({
    queryKey: [QUERY_KEYS.SETTINGS, params?.scope, params?.id],
    queryFn: () => getSettings(params),
    enabled,
  });
};

/** PATCH a location's name/timezone; invalidates the Settings context on success. */
export const useUpdateLocationSettings = () => {
  const queryClient = useQueryClient();
  return useMutation<ApiResponse<LocationSettingsUpdateResult>, unknown, UpdateLocationSettingsInput>({
    mutationFn: (input) => updateLocationSettings(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SETTINGS] });
    },
  });
};

/**
 * Hook to fetch organization-level settings
 * @returns React Query result with organization settings
 */
export const useGetOrganizationSettings = () => {
  return useGetSettings({ scope: "organization" });
};

/**
 * Hook to fetch location-level settings
 * @param locationId - Location ID
 * @param enabled - Enable/disable the query
 * @returns React Query result with location settings
 */
export const useGetLocationSettings = (locationId?: number, enabled: boolean = true) => {
  return useGetSettings(
    { scope: "location", id: locationId },
    enabled && !!locationId
  );
};

/**
 * Hook to fetch machine-level settings
 * @param machineId - Machine ID
 * @param enabled - Enable/disable the query
 * @returns React Query result with machine settings
 */
export const useGetMachineSettings = (machineId?: number, enabled: boolean = true) => {
  return useGetSettings(
    { scope: "machine", id: machineId },
    enabled && !!machineId
  );
};
